// src/components/withProjectPermissions.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import collaborationService from '../services/collaborationService';
import { getAuth } from 'firebase/auth';
import CompanyProjectAccessBanner from './CompanyProjectAccessBanner';

const PERMISSION_CHECK_TIMEOUT_MS = 10000;

/**
 * requireEdit here means "this page has edit actions that need canEdit" — it no longer
 * means "block the page entirely for view-only collaborators". The "Viewer" role
 * (pm_viewer) is a real, intentional collaborator tier — Firestore's own canViewProject()
 * rule already includes it separately from canEditProject(), and writes are independently
 * enforced server-side by role regardless of what renders here. So a viewer without edit
 * rights now still gets the page (with a non-blocking banner), and any edit action they
 * attempt is safely rejected by the existing Firestore rules — this component was the only
 * thing standing between a Viewer and content they were always meant to be able to see.
 */
const withProjectPermissions = (WrappedComponent, options = {}) => {
  const { requireEdit = true } = options;

  return function WithProjectPermissionsComponent(props) {
    const { projectId } = useParams();
    const [permissions, setPermissions] = useState({ canEdit: false, isOwner: false, isCollaborator: false, isCompanyOwner: false });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [timedOut, setTimedOut] = useState(false);

    const runCheck = useCallback(async () => {
      setLoading(true);
      setError(null);
      setTimedOut(false);
      try {
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user) {
          setError('You must be logged in to access this project.');
          return;
        }

        const perms = await collaborationService.checkProjectPermissions(projectId, user.uid);
        setPermissions(perms);

        // Company account owners viewing a project they haven't been invited to are still
        // allowed in (view-only, matches the isCompanyOwnerOfProject Firestore rule) - only
        // block when the user has neither a collaborator status nor company-owner visibility.
        if (!perms.isCollaborator && !perms.isCompanyOwner) {
          setError('You do not have access to this project.');
        }
      } catch (err) {
        console.error('Error checking project permissions:', err);
        setError('Failed to verify project permissions. Please try again.');
      } finally {
        setLoading(false);
      }
    }, [projectId]);

    useEffect(() => {
      if (!projectId) return undefined;
      runCheck();
      const timeoutId = setTimeout(() => setTimedOut(true), PERMISSION_CHECK_TIMEOUT_MS);
      return () => clearTimeout(timeoutId);
    }, [projectId, runCheck]);

    if (loading) {
      return (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <p>Checking permissions...</p>
          {timedOut && (
            <>
              <p style={{ color: '#64748b', fontSize: 14 }}>
                This is taking longer than expected. This might be a network issue.
              </p>
              <button
                onClick={runCheck}
                style={{ background: 'var(--branding-app-primary)', color: 'var(--branding-app-primary-fg)', border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 600, cursor: 'pointer' }}
              >
                Retry
              </button>
            </>
          )}
        </div>
      );
    }

    if (error) {
      return (
        <div style={{
          padding: 40,
          textAlign: 'center',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: 8,
          margin: 20
        }}>
          <h3 style={{ color: '#dc2626', margin: '0 0 16px 0' }}>
            Access Denied
          </h3>
          <p style={{ color: '#991b1b', margin: 0 }}>
            {error}
          </p>
        </div>
      );
    }

    return (
      <>
        {requireEdit && !permissions.canEdit && permissions.isCompanyOwner && (
          <CompanyProjectAccessBanner
            projectId={projectId}
            canEdit={permissions.canEdit}
            isCompanyOwner={permissions.isCompanyOwner}
          />
        )}
        {requireEdit && !permissions.canEdit && !permissions.isCompanyOwner && (
          <div style={{
            padding: '10px 20px',
            textAlign: 'center',
            background: '#f0f9ff',
            borderBottom: '1px solid var(--branding-app-primary)',
            color: '#1e40af',
            fontSize: 13,
            fontWeight: 500,
          }}>
            View-only access — you can browse this project, but only the owner and full collaborators can make changes here.
          </div>
        )}
        <WrappedComponent {...props} permissions={permissions} />
      </>
    );
  };
};

export default withProjectPermissions;
